import { emitEvent } from '@faceup/devtools'
import { sharedMessages, useIntl } from '@faceup/localization'
import { configureTrpcClient, isApiError, QueryClientProvider, queryClient } from '@faceup/trpc'
import { useNotification } from '@faceup/ui-base'
import { DEFAULT_LANGUAGE, regionHeaderName, type SupportedRegion } from '@faceup/utils'
import * as Sentry from '@sentry/react'
import { type ReactNode, useEffect, useRef } from 'react'
import { Auth } from './utils/auth'

// kredenc selects its region per-view (not from the JWT like akutan), so the active region is pushed
// into this module-level holder by the provider and read live by `getHeaders`.
const dynamicHeaderValues = {
  region: null as SupportedRegion | null,
}

type ErrorHandlerRefs = {
  apiErrorMessage: string
  notification: ReturnType<typeof useNotification> | null
}

const errorHandlerRefs: ErrorHandlerRefs = {
  apiErrorMessage: '',
  notification: null,
}

// kredenc has no AuthProvider/useAuth — log out the same way its Apollo errorLink does.
const logoutAndRedirect = () => {
  queryClient.clear()
  Auth.logout()
  window.location.replace('/')
}

const isAuthError = (errorMessage: string) =>
  errorMessage.includes('UNAUTHORIZED') || errorMessage.includes('Outdated or corrupted JWT token')

configureTrpcClient({
  url: import.meta.env.VITE_API_URL?.replace('graphql', 'trpc') ?? '',
  getHeaders: () => {
    const token = Auth.getJwt()
    return {
      authorization: token ? `Bearer ${token}` : '',
      language: DEFAULT_LANGUAGE,
      ...(dynamicHeaderValues.region ? { [regionHeaderName]: dynamicHeaderValues.region } : {}),
    }
  },
  onRequest:
    import.meta.env.VITE_ENVIRONMENT !== 'production'
      ? info => {
          emitEvent('requests', {
            name: info.path,
            query: null,
            operationType: info.type,
            variables: info.input,
            response: info.response,
            httpAnalyzer: { duration: info.durationMs },
            isError: info.isError,
            source: 'trpc',
            apiUrl: info.apiUrl,
          })
        }
      : undefined,
  onMutationError: error => {
    const errorMessage = error instanceof Error ? error.message : String(error ?? 'Unknown error')

    if (isAuthError(errorMessage)) {
      logoutAndRedirect()
      return
    }

    errorHandlerRefs.notification?.error({
      message: errorHandlerRefs.apiErrorMessage,
      description: errorMessage,
    })

    if (!isApiError(error)) {
      Sentry.captureException(error, { tags: { source: 'trpc-mutation' } })
    }
  },
  onQueryError: error => {
    const errorMessage = error instanceof Error ? error.message : String(error ?? 'Unknown error')

    if (isAuthError(errorMessage)) {
      logoutAndRedirect()
      return
    }

    if (!isApiError(error)) {
      Sentry.captureException(error, { tags: { source: 'trpc-query' } })
    }
  },
})

queryClient.setDefaultOptions({
  queries: {
    retry: 1,
    refetchOnWindowFocus: false,
  },
})

type TrpcProviderProps = {
  children: ReactNode
  region: SupportedRegion | null
}

export const TrpcProvider = ({ children, region }: TrpcProviderProps) => {
  const { formatMessage } = useIntl()
  const notification = useNotification()

  const apiErrorMessageRef = useRef(formatMessage(sharedMessages.apiError))
  const notificationRef = useRef(notification)
  apiErrorMessageRef.current = formatMessage(sharedMessages.apiError)
  notificationRef.current = notification

  useEffect(() => {
    errorHandlerRefs.apiErrorMessage = apiErrorMessageRef.current
    errorHandlerRefs.notification = notificationRef.current
  })

  const previousRegionRef = useRef(region)
  useEffect(() => {
    dynamicHeaderValues.region = region
    if (previousRegionRef.current === region) {
      return
    }
    previousRegionRef.current = region
    // Query keys carry no region, so a mounted page would keep the previous region's data. Reset
    // only after the header above is updated, or the refetch would still go to the old region.
    queryClient.resetQueries()
  }, [region])

  // hack copied from akutan/tRPCProvider
  // helps deal with stale data but isn't a reaction to any particular problem
  useEffect(() => {
    return queryClient.getQueryCache().subscribe(event => {
      if (event.type === 'observerRemoved' && event.query.getObserversCount() === 0) {
        queryClient.removeQueries({ queryKey: event.query.queryKey })
      }
    })
  }, [])

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
